"use client";
import styles from "./MultipleNMIAddressSelect.module.scss";
import { useState } from "react";
import { Button } from "../../Button/Button";
import { track } from "@/utilities/analytics";
import { siteAPIURL } from "@/utilities/constants";
import { FormProvider, useForm } from "react-hook-form";
import { getPlansbyNMI } from "../ProductResultsActions";
import { RadioGroupField } from "../../../form/fields/RadioGroupField/RadioGroupField";
import { NmiSearchModal } from "../../NmiSearchModal/NmiSearchModal";
import { InfoCard } from "./InfoCard/InfoCard";
import { StickyBar } from "./StickyBar/StickyBar";
import type { MultipleNmiAddressSelectContent, NmiSearchModalContent } from "@/sanity/queries";
import { useRouter } from "next/navigation";

interface MultipleNMIAddressSelectProps {
	nmis: {
		address: string;
		nmi: string;
	}[];
	setData: any;
	promo?: string;
	nmiSearchModalContent?: NmiSearchModalContent | null;
	content?: MultipleNmiAddressSelectContent | null;
}

interface PlanResultsResponse {
	catalogueProductsResponse?: any;
	nmiDetailResponse?: any;
	tarrifs?: string[];
	[key: string]: any;
}

export const MultipleNMIAddressSelect = ({
	nmis,
	setData,
	promo,
	nmiSearchModalContent,
	content,
}: MultipleNMIAddressSelectProps) => {
	const router = useRouter();
	const [formProcessing, setFormProcessing] = useState(false);
	const [isNmiModalOpen, setIsNmiModalOpen] = useState(false);

	const formMethods = useForm<{ nmi: string }>({
		defaultValues: {},
		reValidateMode: "onChange",
		shouldFocusError: false,
	});

	const { handleSubmit, register } = formMethods;

	const onSubmit: Parameters<typeof handleSubmit>[0] = async data => {
		setFormProcessing(true);

		track({ event: "address_disambiguated", energy: "electricity" });

		const getPlansResult = await getPlansbyNMI(data.nmi, promo);
		setData(getPlansResult);

		setFormProcessing(false);
	};

	const handleNmiSearch = (nmi: string) => {
		track({ event: "address_search", search_type: "nmi", energy: "electricity" });

		const queryParams = new URLSearchParams();

		queryParams.append("nmi", nmi);
		queryParams.append("energy", "electricity");

		if (promo) {
			queryParams.append("promo", promo);
		}

		queryParams.append("requestID", crypto.randomUUID());
		router.push(`/apply-online?${queryParams.toString()}#product-result`);
	};

	const displayContent = getDisplayContent(content);

	return (
		<div className={styles["nmi-address-select"]}>
			<div className={styles["nmi-address-select__body"]}>
				<h2 className={styles["nmi-address-select__heading"]}>{displayContent.heading}</h2>
				<div className={styles["nmi-address-select__intro"]}>
					{displayContent.intro.map((paragraph, index) => (
						<p key={index}>{paragraph}</p>
					))}
				</div>
				<aside className={styles["nmi-address-select__tips"]}>
					<h3 className={styles["nmi-address-select__tips-heading"]}>
						{displayContent.tips.heading}
					</h3>
					<p>{displayContent.tips.text}</p>
					<ul className={styles["nmi-address-select__tips-list"]}>
						{displayContent.tips.items.map((item, index) => (
							<li key={index}>{item}</li>
						))}
					</ul>
				</aside>
			</div>
			<div className={styles["nmi-address-select__form-wrap"]}>
				<FormProvider {...formMethods}>
					<form
						onSubmit={handleSubmit(onSubmit)}
						className={styles["nmi-address-select__card"]}
					>
						<header className={styles["nmi-address-select__card-header"]}>
							<h3 className={styles["nmi-address-select__card-heading"]}>
								{displayContent.selectAddressHeading}
							</h3>
							<span
								className={styles["nmi-address-select__nmi-header"]}
								id="nmi-header"
							>
								{displayContent.nmiColumnHeading}
							</span>
						</header>
						<div className={styles["nmi-address-select__addresses"]}>
							<RadioGroupField
								register={register}
								registerOptions={{ required: true }}
								name="nmi"
								label="Select your address"
								hideLabel
								innerClassName={styles["nmi-address-select__radio-group"]}
								optionClassName={styles["nmi-address-select__radio-group-option"]}
								labelClassName={styles["nmi-address-select__radio-group-label"]}
								options={nmis.map((data: any) => ({
									label: (
										<>
											<span aria-label="Address">{data.address}</span>
											<div>
												<span className={styles["nmi-address-select__nmi-label"]}>NMI: </span>
												<span className={styles["nmi-address-select__nmi"]}>
													{data.nmi.slice(0, 6).replace(/./g, "*") + data.nmi.slice(6, 11)}
												</span>
											</div>
										</>
									),
									value: data.nmi,
								}))}
							/>
						</div>

						<Button
							className={styles["nmi-address-select__submit"]}
							type="submit"
							showArrow
							disabled={formProcessing}
							label={
								formProcessing
									? displayContent.submitButtonLoadingLabel
									: displayContent.submitButtonLabel
							}
						/>
					</form>
				</FormProvider>

				<StickyBar
					heading={displayContent.stickyBar.heading}
					text={displayContent.stickyBar.text}
					buttonLabel={displayContent.stickyBar.buttonLabel}
					image={displayContent.stickyBar.image}
					onSearchClick={() => setIsNmiModalOpen(true)}
				/>
			</div>

			{displayContent.infoCards.length > 0 && (
				<div className={styles["nmi-address-select__info"]}>
					{displayContent.infoCards.map(card => (
						<InfoCard
							key={card._key}
							heading={card.heading}
							image={card.image}
							buttonLabel={card.buttonLabel}
							buttonHref={card.buttonHref}
						>
							<p>{card.text}</p>
						</InfoCard>
					))}
				</div>
			)}

			<NmiSearchModal
				isOpen={isNmiModalOpen}
				content={nmiSearchModalContent}
				onOpenChange={setIsNmiModalOpen}
				onNmiSearch={handleNmiSearch}
			/>
		</div>
	);
};

const defaultContent = {
	heading: "We've found multiple addresses",
	intro: [
		"Looks like we've found more than one place to connect to at your address. Please choose the correct one that matches your location.",
		"To help choose the right one, NMI numbers have also been included, which can be located on your last bill.",
	],
	tips: {
		heading: "Tips to finding your connection",
		text: "Sometimes the connection address doesn't quite match up, if you can't find your address, please try the following:",
		items: [
			'Use "Unit" instead of "U", e.g Unit 9',
			"Use A, B etc. if you are on a subdivided block, e.g 9A",
			"Lorem ipsum dolor set amit",
		],
	},
	selectAddressHeading: "Select your address",
	nmiColumnHeading: "NMI",
	submitButtonLabel: "Continue",
	submitButtonLoadingLabel: "Loading plans",
	stickyBar: {
		heading: "Enter NMI manually",
		text: "Find your connection using your connection National Meter Identifier (NMI).",
		buttonLabel: "Search NMI",
		image: null,
	},
	infoCards: [
		{
			_key: "default-nmi-number",
			heading: "I don't know my NMI number",
			text: "If you can't locate the right address, and aren't having any luck with your NMI number, contact us today and we'll call you back to get connected.",
			image: null,
			buttonLabel: "Contact Solstice",
			buttonHref: "/contact-us",
		},
		{
			_key: "default-moving-house",
			heading: "Moving house?",
			text: "We understand moving can be hard. If you can't locate the right address, or don't know your new NMI number, please get in touch today and we'll help you get connected.",
			image: null,
			buttonLabel: "Contact Solstice",
			buttonHref: "/contact-us",
		},
	],
};

type MultipleNmiAddressSelectInfoCard = NonNullable<
	MultipleNmiAddressSelectContent["infoCards"]
>[number];

const hasCta = (cta: MultipleNmiAddressSelectInfoCard["contactCta"]) => {
	return Boolean((cta?.showCta ?? true) && cta?.label && cta?.link);
};

const compactStrings = (items?: string[] | null) => {
	const compacted = items?.filter(Boolean);
	return compacted?.length ? compacted : null;
};

const getInfoCards = (content?: MultipleNmiAddressSelectContent | null) => {
	if (!content) {
		return defaultContent.infoCards;
	}

	if (!content.infoCards?.length) {
		return [];
	}

	return content.infoCards.map((card, index) => {
		const fallbackCard = defaultContent.infoCards[index] || defaultContent.infoCards[0];
		const contactCta = hasCta(card.contactCta) ? card.contactCta : null;

		return {
			_key: card._key || fallbackCard._key,
			heading: card.heading || fallbackCard.heading,
			text: card.text || fallbackCard.text,
			image: card.image || fallbackCard.image,
			buttonLabel: contactCta?.label || fallbackCard.buttonLabel,
			buttonHref: contactCta?.link || fallbackCard.buttonHref,
		};
	});
};

const getDisplayContent = (content?: MultipleNmiAddressSelectContent | null) => ({
	heading: content?.heading || defaultContent.heading,
	intro: compactStrings(content?.intro) || defaultContent.intro,
	tips: {
		heading: content?.tips?.heading || defaultContent.tips.heading,
		text: content?.tips?.text || defaultContent.tips.text,
		items: compactStrings(content?.tips?.items) || defaultContent.tips.items,
	},
	selectAddressHeading: defaultContent.selectAddressHeading,
	nmiColumnHeading: defaultContent.nmiColumnHeading,
	submitButtonLabel: defaultContent.submitButtonLabel,
	submitButtonLoadingLabel: defaultContent.submitButtonLoadingLabel,
	stickyBar: {
		heading: content?.stickyBar?.heading || defaultContent.stickyBar.heading,
		text: content?.stickyBar?.text || defaultContent.stickyBar.text,
		buttonLabel: content?.stickyBar?.buttonLabel || defaultContent.stickyBar.buttonLabel,
		image: content?.stickyBar?.image || defaultContent.stickyBar.image,
	},
	infoCards: getInfoCards(content),
});

export const getPlans = async (nmi: string, promo?: string): Promise<PlanResultsResponse> => {
	const url = new URL(siteAPIURL + "/signup/get-plans");
	url.searchParams.append("nmi", nmi);
	if (promo) {
		url.searchParams.append("promo", promo);
	}

	return fetch(url)
		.then(response => response.json())
		.catch(error => {
			console.error("Failed to get plans", error);
			return {};
		});
};
